import { useState, useEffect } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Settings, LogOut, Menu, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [username, setUsername] = useState("");
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("wiki_token");
    
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/wiki/verify?token=${token}`);
      if (response.data.valid) {
        setIsAuthenticated(true);
        setUsername(response.data.username);
      } else {
        localStorage.removeItem("wiki_token");
        localStorage.removeItem("wiki_username");
      }
    } catch (error) {
      console.error("Auth check error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);

    try {
      const response = await axios.post(`${API}/wiki/login`, loginForm);
      if (response.data.token) {
        localStorage.setItem("wiki_token", response.data.token);
        localStorage.setItem("wiki_username", loginForm.username);
        setIsAuthenticated(true);
        setUsername(loginForm.username);
        toast.success("Logged in successfully");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("wiki_token");
    localStorage.removeItem("wiki_username");
    setIsAuthenticated(false);
    setUsername("");
    toast.success("Logged out successfully");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f6f6f6] flex items-center justify-center">
        <div className="text-[#72777d]">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#f6f6f6] flex items-center justify-center">
        <div className="bg-white border border-[#a2a9b1] p-8 w-full max-w-md">
          <div className="flex items-center gap-3 mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_df6f992c-82a6-44fb-a88f-450c0ac99b5b/artifacts/cvze4lab_q2y0upqj_75c9mfk0_Square%20Logo%20%28100%20x%20100%29.png" 
              alt="Ethical Wiki" 
              className="w-10 h-10"
            />
            <h1 className="font-wiki text-2xl text-[#202122]">Admin Login</h1>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm text-[#202122] mb-1">Username</label>
              <input
                type="text"
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                className="w-full px-3 py-2 border border-[#a2a9b1] focus:border-[#3366cc] focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-[#202122] mb-1">Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className="w-full px-3 py-2 border border-[#a2a9b1] focus:border-[#3366cc] focus:outline-none"
                required
              />
            </div>
            <button
              type="submit"
              disabled={isLoggingIn}
              className="w-full bg-[#3366cc] text-white py-2 px-4 hover:bg-[#2a4b8d] disabled:opacity-50"
            >
              {isLoggingIn ? "Logging in..." : "Log in"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const navItems = [
    { path: "/admin/submissions", label: "View Submissions", icon: FileText },
    { path: "/admin/optimize", label: "Optimize Review", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#f6f6f6] flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-white border-r border-[#a2a9b1] flex-shrink-0 transition-all duration-300`}>
        <div className="p-4 border-b border-[#a2a9b1] flex items-center justify-between">
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_df6f992c-82a6-44fb-a88f-450c0ac99b5b/artifacts/cvze4lab_q2y0upqj_75c9mfk0_Square%20Logo%20%28100%20x%20100%29.png" 
                alt="Ethical Wiki" 
                className="w-8 h-8"
              />
              <span className="font-wiki text-lg text-[#202122]">Admin</span>
            </div>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1 hover:bg-[#eaecf0] rounded"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        
        <nav className="p-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
              (item.path === '/admin/submissions' && location.pathname === '/admin');
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2 rounded mb-1 transition-colors ${
                  isActive 
                    ? 'bg-[#eaf3ff] text-[#3366cc]' 
                    : 'text-[#202122] hover:bg-[#eaecf0]'
                }`}
              >
                <Icon className="w-5 h-5" />
                {sidebarOpen && <span>{item.label}</span>}
              </Link>
            );
          })}
          
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 rounded w-full text-left text-[#202122] hover:bg-[#eaecf0] mt-4"
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span>Log out</span>}
          </button>
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6 overflow-auto">
        <div className="max-w-6xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
